import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './QTcCalculator.scss';
import CommonLayout from '../../components/CommonLayout';
import AppsHeader from '../components/AppsHeader';

type Formula = 'bazett' | 'fridericia' | 'framingham';

const QTcCalculator: React.FC = () => {
  const { t } = useTranslation('common');
  const [qtBoxes, setQtBoxes] = useState<string>('');
  const [heartRate, setHeartRate] = useState<string>('');
  const [formula, setFormula] = useState<Formula>('bazett');
  const [qtcResult, setQtcResult] = useState<number | null>(null);

  // Add reset handler
  const handleReset = () => {
    setQtBoxes('');
    setHeartRate('');
    setFormula('bazett'); // Reset formula to default
    setQtcResult(null);
  };

  useEffect(() => {
    const qtBoxesNum = parseInt(qtBoxes, 10);
    const hrNum = parseInt(heartRate, 10);

    if (!isNaN(qtBoxesNum) && qtBoxesNum > 0 && !isNaN(hrNum) && hrNum > 0) {
      const qtMs = qtBoxesNum * 40; // Each box is 40ms
      const rrSec = 60 / hrNum;
      let qtc: number;

      switch (formula) {
        case 'fridericia':
          qtc = qtMs / Math.cbrt(rrSec);
          break;
        case 'framingham':
          qtc = qtMs + 154 * (1 - rrSec);
          break;
        case 'bazett':
        default:
          qtc = qtMs / Math.sqrt(rrSec);
          break;
      }
      setQtcResult(Math.round(qtc)); // Round to nearest integer
    } else {
      setQtcResult(null); // Reset if inputs are invalid
    }
  }, [qtBoxes, heartRate, formula]);

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      // Allow only non-negative integers
      if (/^\d*$/.test(value)) {
        setter(value);
      }
    };

  return (
    <div>
      {/* Add dir="rtl" to match AnionGap */}
      <div className="qtc-calculator container main-content qtc-calculator-container" dir="rtl">
        <AppsHeader 
          title={t('qtcCalculator.qtcCalculatorTitle')}
          onReset={handleReset}
        />

        <div className="qtc-inputs">
          <div className="form-grid">
            <label className="grid-label" htmlFor="qtBoxes">{t('qtcCalculator.qtIntervalLabel')}:</label>
            <div className="grid-input">
              <input
                type="number"
                id="qtBoxes"
                value={qtBoxes}
                onChange={handleInputChange(setQtBoxes)}
                min="0"
                step="1"
                placeholder={t('enterValue')}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-grid">
            <label className="grid-label" htmlFor="heartRate">{t('qtcCalculator.heartRateLabel')}:</label>
            <div className="grid-input">
              <input
                type="number"
                id="heartRate"
                value={heartRate}
                onChange={handleInputChange(setHeartRate)}
                min="0"
                step="1"
                placeholder={t('enterValue')}
                className="form-control"
              />
            </div>
          </div>
        </div>

        <div className="formula-selection">
          <div className="form-grid">
            <label className="grid-label">{t('qtcCalculator.formulaLabel')}:</label>
            <div className="grid-input">
              <div className="radio-group">
                {(['bazett', 'fridericia', 'framingham'] as Formula[]).map((f) => (
                  <div key={f} className="radio-item">
                    <input
                      type="radio"
                      name="formula"
                      value={f}
                      checked={formula === f}
                      onChange={() => setFormula(f)}
                      className="form-check-input custom-radio"
                    />
                    <label className="form-check-label" htmlFor={f}>
                      {t(`qtcCalculator.${f}`)} {/* Use nested keys */}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {qtcResult !== null && (
          <div className="result-display">
            <h3>{t('qtcCalculator.resultLabel')}:</h3>
            <p>{qtcResult} ms</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QTcCalculator;